import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Player from '@vimeo/player';
import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';
import PlayButtonInverse from 'dibs-icons/exports/legacy/PlayButtonInverse';
import { Link } from 'dibs-elements/exports/Link';

import tracking from './tracking';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './PdpSharedVimeoVideo.scss';

export const PdpSharedVimeoVideo: FC<{
    id: number;
    fallback?: ReactNode;
    useDefaultControls?: boolean;
}> = ({ id, fallback, useDefaultControls = false }) => {
    const playerContainerRef = useRef<HTMLDivElement>(null);

    const playerRef = useRef<Player>();
    const trackingStateRef = useRef(tracking.initialTrackingState);
    const isInViewPort = useRef(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isPermissionsDenied, setIsPermissionsDenied] = useState(false);

    const onPlay = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.play().catch(() => {
                //if error occurs, it's most likely due to the user device being in
                //Low Power Mode and requiring user manual interaction to play video
                setIsPermissionsDenied(true);
            });
        }
    }, []);

    const onPause = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.pause();
        }
    }, []);

    useEffect(() => {
        if (playerContainerRef.current && !playerRef.current) {
            const height = playerContainerRef.current.offsetHeight - 18;
            const width = playerContainerRef.current.offsetWidth;

            playerRef.current = new Player(playerContainerRef.current, {
                id,
                autoplay: true,
                loop: true,
                muted: true,
                byline: false,
                autopause: false,
                controls: useDefaultControls,
                quality: '720p',
                width,
                height,
                title: false,
            });
        }

        const loaded = (): void => {
            setIsLoaded(true);
        };
        const play = (): void => {
            if (isPermissionsDenied && !useDefaultControls) {
                //some browsers autoplay the video without user interaction when permissions are denied or error occurs
                setIsPermissionsDenied(false);
            }
        };
        const pause = (): void => {
            if (
                !(
                    document.fullscreenElement ||
                    // @ts-ignore
                    document.webkitFullscreenElement ||
                    // @ts-ignore
                    document.msFullscreenElement
                ) &&
                isInViewPort.current &&
                !isPermissionsDenied &&
                !useDefaultControls
            ) {
                onPlay();
            } else {
                tracking.videoPaused({ vimeoId: id });
            }
        };
        const timeupdate = tracking.percentCompleted(trackingStateRef, id);

        if (playerRef.current) {
            playerRef.current.on('loaded', loaded);
            playerRef.current.on('play', play);
            playerRef.current.on('pause', pause);
            playerRef.current.on('timeupdate', timeupdate);
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.off('loaded', loaded);
                playerRef.current.off('play', play);
                playerRef.current.off('pause', pause);
                playerRef.current.off('timeupdate', timeupdate);
            }
        };
    }, [id, useDefaultControls, onPlay, onPause, isPermissionsDenied]);

    const onPermissionsClick = useCallback(() => {
        //After a click on video in Low Power Mode user gives permission to manipulate video
        setIsPermissionsDenied(false);
        onPlay();
    }, [onPlay]);

    const onFullScreenClick = useCallback(() => {
        if (playerRef.current) {
            // @ts-ignore @types/vimeo__player is not up to date with the latest @vimeo/player version
            playerRef.current.requestFullscreen();
        }
    }, []);

    const onVisibilityChange = useCallback(
        ({ isVisible }: VisibilityChangeCallback): void => {
            if (playerRef.current && !isPermissionsDenied) {
                if (isVisible) {
                    onPlay();
                } else {
                    onPause();
                }
            }
            isInViewPort.current = isVisible;
        },
        [isPermissionsDenied, onPlay, onPause]
    );

    return (
        <div
            className={classnames(
                dibsCss.relative,
                dibsCss.wFull,
                dibsCss.hFull,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter
            )}
        >
            {!isLoaded && (
                <div
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        dibsCss.top0,
                        dibsCss.left0,
                        dibsCss.bgOverlaylight
                    )}
                >
                    {fallback}
                </div>
            )}

            {isLoaded && isPermissionsDenied && !useDefaultControls && (
                <Link
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.top0,
                        dibsCss.left0
                    )}
                    onClick={onPermissionsClick}
                >
                    <div
                        className={classnames(
                            dibsCss.wFull,
                            dibsCss.hFull,
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.justifyCenter,
                            dibsCss.bgOverlaylight
                        )}
                    >
                        <PlayButtonInverse
                            className={classnames(dibsCss.hXlarge, dibsCss.wXlarge)}
                        />
                    </div>
                </Link>
            )}

            {isLoaded && !isPermissionsDenied && !useDefaultControls && (
                <Link
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.top0,
                        dibsCss.left0
                    )}
                    onClick={onFullScreenClick}
                >
                    <div className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.flex)} />
                </Link>
            )}

            <div
                data-tn="video"
                className={classnames(
                    { [styles.video]: !useDefaultControls },
                    dibsCss.wFull,
                    dibsCss.hFull,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter
                )}
                ref={playerContainerRef}
            />
            <VisibilityTracker
                watchAfterFirstVisible
                elementRef={playerContainerRef}
                onVisibilityChange={onVisibilityChange}
                observerOptions={{
                    threshold: 0.1,
                }}
            />
        </div>
    );
};
